import { Show } from "@refinedev/mui";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Link,
  List,
  ListItem,
  listItemClasses,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  IResourceComponentsProps,
  useCustom,
  useDelete,
  useShow,
} from "@refinedev/core";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import { getPercentage, moneyFormat } from "utils/money-format";
import { longDateFormat, numberOfDays, shortFormat } from "utils/date-format";
import { useState } from "react";
import { UpdateMarketValueModal } from "components/modal/update-market-value-modal";
import { warm } from "style/color";
import MDEditor from "@uiw/react-md-editor";
import { useNavigate, useParams } from "react-router-dom";
import SettingsIcon from "@mui/icons-material/Settings";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { GrantvestmentUpdatesVisibilityModal } from "components/modal/grantvestment-update-visibility";
import { GrantVestmentAuthorizedViewers } from "interfaces/grant-vestment";
import { GrantVestmentRequestsList } from "components/list/grantvestment-requests-list";
import { GrantVestmentLikesList } from "components/list/grantvestment-likes-list";
import UserAvatar from "components/card/UserAvatar";
import NotifyUserModal from "components/modal/notify-user-modal";
import { GrantVestmentFollowersList } from "components/list/grantvestment-followers-list";
import rehypeSanitize from "rehype-sanitize";
import remarkBreaks from "remark-breaks";
import { GrantVestmentDistributionList } from "./gv-distribuion-list";

export const GrantVestmentDetails: React.FC<IResourceComponentsProps> = () => {
  const { id } = useParams();
  const { queryResult } = useShow();
  const { data, isLoading, refetch } = queryResult;
  const { data: requests } = useCustom({
    url: `total-grantvestments/${id}`,
    method: "get",
    config: {
      filters: [
        {
          field: "date",
          operator: "eq",
          value: new Date().toISOString().substring(0, 10),
        },
      ],
    },
  });
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [updateVisibilityModalOpen, setUpdateVisibilityModalOpen] =
    useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);

  const record = data?.data;
  const funded = getPercentage(record?.raisedAmount, record?.goalAmount);
  const daysLeft =
    numberOfDays(record?.deadline) < 0 ? 0 : numberOfDays(record?.deadline);

  const handleTabChange = (e: any, tabIndex: number) => {
    setCurrentTabIndex(tabIndex);
  };

  const { mutate, isLoading: loadingDelete } = useDelete();
  const navigate = useNavigate();
  const noData = () => {
    return (
      <Box sx={{ textAlign: "center", my: 9 }}>
        <Typography
          sx={{ color: "neutral.main" }}
          variant="subtitle3"
          gutterBottom
          paragraph
        >
          There's no data available
        </Typography>
      </Box>
    );
  };

  const handleDelete = (item: GrantVestmentAuthorizedViewers) => {
    mutate(
      {
        resource: `grant-vestments`,
        id: `${item.grantVestmentAuthorizedViewersId}/unauthorize`,
      },
      {
        onSuccess: () => {
          refetch();
        },
      }
    );
  };
  return (
    <Show
      title={""}
      goBack={""}
      isLoading={isLoading}
      canEdit
      headerButtons={({ defaultButtons, editButtonProps }) => (
        <>
          {defaultButtons}
          {editButtonProps && (
            <Button
              disabled={isLoading}
              onClick={() => navigate(`/grant-vestments/create?duplicate_from=${id}`)}
              startIcon={<ContentCopyIcon />}
              variant="text"
              color="primary"
            >
              Duplicate
            </Button>
          )}
        </>
      )}
    >
      {queryResult?.status === "loading" ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <>
          <Box sx={{ m: 1, mt: 0 }}>
            <Grid container>
              <Grid xs={12} sm={12} md={10} paddingRight={{ md: 2 }}>
                <Typography
                  sx={{ color: "neutral.main" }}
                  variant="h3"
                  gutterBottom
                >
                  {record?.name}
                </Typography>
              </Grid>

              <Grid
                xs={12}
                sm={12}
                md={12}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <>
                  {(record?.status === "CLOSED" ||
                    record?.status === "RAISING") && (
                    <Button
                      size="large"
                      variant="contained"
                      sx={{
                        m: 2,
                      }}
                      onClick={() =>
                        navigate(`/grant-vestments/${id}/distributions`)
                      }
                      disabled={requests?.data.funds.length === 0}
                    >
                      New distribution
                    </Button>
                  )}
                  {record?.status === "CLOSED" && (
                    <Button
                      size="large"
                      variant="contained"
                      sx={{
                        m: 2,
                      }}
                      onClick={() => setConfirmationModalOpen(true)}
                    >
                      Market value
                    </Button>
                  )}
                </>
              </Grid>
            </Grid>
            <Typography variant="bodyCopyRegular" paragraph>
              {record?.description}
            </Typography>
            <Typography
              variant="bodyCopyBold"
              sx={{ color: "neutral.main", mb: 5 }}
              paragraph
            >
              {record?.projectBy}
            </Typography>
            <Chip label={record?.category.categoryName} />

            <Card
              className="single"
              sx={{
                backgroundColor: "transparent",
                mt: 5,
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                width: "100%",
                maxWidth: "100%",
                overflow: "hidden",
              }}
            >
              <CardMedia
                component="img"
                sx={{
                  width: { xs: "100%", md: "33%" },
                  height: { xs: "250px", md: "auto" },
                  minHeight: { md: "300px" },
                  objectFit: "contain",
                  objectPosition: "center",
                  "&:hover": { cursor: "pointer" },
                  flex: { xs: "none", md: "0 0 33%" },
                }}
                image={record?.imageUrl}
                alt="Grant vestment project image"
              />

              <Box
                sx={{
                  ml: { xs: 0, md: 2 },
                  mt: { xs: 2, md: 0 },
                  width: { xs: "100%", md: "60%" },
                  flexGrow: 1,
                }}
              >
                <CardContent>
                  <Typography
                    variant="smallCopy"
                    className="title"
                    sx={{ color: "neutral.lighter" }}
                    paragraph
                  >
                    Raised
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{ color: "neutral.main" }}
                    gutterBottom
                  >
                    ${moneyFormat(record?.raisedAmount!)}
                    <Typography
                      variant="subtitle3"
                      sx={{
                        float: "right",
                        display: { xs: "none", md: "block" },
                      }}
                    >
                      ${moneyFormat(record?.goalAmount!)} goal
                    </Typography>
                  </Typography>

                  {/* Progress Bar */}
                  <Box sx={{ width: "100%", my: 2 }}>
                    <LinearProgress
                      color="primary"
                      variant="determinate"
                      value={parseFloat(funded) > 100 ? 100 : parseInt(funded)}
                    />
                  </Box>
                  <Typography
                    variant="smallCopy"
                    color={"primary"}
                    className="title"
                    paragraph
                  >
                    {funded}% funded
                  </Typography>

                  {/* Days Left */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{ mb: 1 }}
                  >
                    <AccessTimeIcon />
                    <Typography
                      variant="bodyCopyBold"
                      sx={{ color: "neutral.main" }}
                    >
                      {daysLeft} days to go - until{" "}
                      {shortFormat(record?.deadline)}
                    </Typography>
                  </Stack>

                  {/* Grant Vestors */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{ mb: 1 }}
                  >
                    <PeopleOutlineIcon />
                    <Typography
                      variant="bodyCopyBold"
                      sx={{ color: "neutral.main" }}
                    >
                      {record?.grantVestorCounter}
                    </Typography>
                  </Stack>

                  {record?.allOrNothing ? (
                    <Typography
                      variant="smallCopy"
                      sx={{ color: "neutral.lighter" }}
                      paragraph
                    >
                      This project will only be funded if it reaches its goal by{" "}
                      {longDateFormat(record?.deadline)}.
                    </Typography>
                  ) : (
                    <Typography
                      variant="smallCopy"
                      sx={{ color: "neutral.lighter" }}
                      paragraph
                    >
                      This project can be funded even if it doesn't reach its
                      goal by {longDateFormat(record?.deadline)}.
                    </Typography>
                  )}

                  {/* Key Dates */}
                  <Typography
                    variant="smallCopy"
                    sx={{ color: "neutral.lighter" }}
                    paragraph
                  >
                    Deadline: {longDateFormat(record?.deadline)}
                  </Typography>
                  <Typography
                    variant="smallCopy"
                    sx={{ color: "neutral.lighter" }}
                    paragraph
                  >
                    Launched at: {longDateFormat(record?.launchedAt)}
                  </Typography>
                  <Typography
                    variant="smallCopy"
                    sx={{ color: "neutral.lighter" }}
                    paragraph
                  >
                    Non DAF: {moneyFormat(record?.nonDAF)}
                  </Typography>
                  {record?.status === "CLOSED" && (
                    <>
                      <Typography
                        variant="smallCopy"
                        sx={{ color: "neutral.lighter" }}
                        paragraph
                      >
                        Closed at: {longDateFormat(record?.closedAt)}
                      </Typography>
                      <Typography
                        variant="smallCopy"
                        sx={{ color: "neutral.lighter" }}
                        paragraph
                      >
                        RVPI: {record?.moicValue ? record?.moicValue : 0}
                      </Typography>
                    </>
                  )}

                  {/* Fees */}
                  <Typography
                    variant="smallCopy"
                    sx={{ color: "neutral.lighter" }}
                    paragraph
                  >
                    Upfront fee: {moneyFormat(record?.upFrontFee)}
                  </Typography>
                  <Typography
                    variant="smallCopy"
                    sx={{ color: "neutral.lighter" }}
                    paragraph
                  >
                    Upfront percentage fee:{" "}
                    {moneyFormat(record?.upFrontPercentageFee)}
                  </Typography>
                  <Typography
                    variant="smallCopy"
                    sx={{ color: "neutral.lighter" }}
                    paragraph
                  >
                    Per annum fee:{" "}
                    {record?.perAnnumFee === -1
                      ? `Standard`
                      : moneyFormat(record?.perAnnumFee)}
                  </Typography>
                </CardContent>
              </Box>
            </Card>
            <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
              <Grid item xs={12} sm={6}>
                {record?.tags && record?.tags.length > 0 ? (
                  record?.tags.map((tag: any) => {
                    return (
                      <Chip
                        sx={{ mr: 2 }}
                        key={tag.tagId}
                        label={tag.tagName}
                      />
                    );
                  })
                ) : (
                  <Typography
                    variant="smallCopyBold"
                    sx={{ color: "neutral.lighter", fontWeight: 700 }}
                  >
                    No Tags
                  </Typography>
                )}
              </Grid>
            </Grid>
            <Tabs
              value={currentTabIndex}
              onChange={handleTabChange}
              className="record"
            >
              <Tab label="About" className="record" />
              <Tab label="Data room " className="record" />
              <Tab label="Updates" className="record" />
              <Tab label="Market value history" className="record" />
              <Tab label="GrantVestment requests" className="record" />
              <Tab label="Distributions" className="record" />
              <Tab label="Updates visibility" className="record" />
              <Tab label="Likes" className="record" />
              <Tab label="Followers" className="record" />
            </Tabs>

            <CustomTabPanel value={currentTabIndex} index={0}>
              <div
                data-color-mode="light"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  width: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  <MDEditor.Markdown
                    style={{
                      backgroundColor: "transparent",
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      wordBreak: "break-all",
                      width: "100%",
                      padding: "5px",
                      margin: 0,
                      boxSizing: "border-box",
                    }}
                    source={record?.about}
                    rehypePlugins={[[rehypeSanitize]]}
                    remarkPlugins={[[remarkBreaks]]}
                    components={{
                      p: ({ node, ...props }) => {
                        return <p {...props} style={{
                          margin: 0,
                          padding: 0,
                          marginBottom: '0.5em'
                        }} />;
                      },
                      ul: (props) => <ul {...props} style={{
                        margin: 0,
                        marginTop: '0.25em',
                        marginBottom: '0.75em',
                        paddingLeft: '1.5em'
                      }} />,
                      li: (props) => <li {...props} style={{ margin: 0, padding: 0, lineHeight: 1.5 }} />
                    }}
                  />
                </div>
              </div>
            </CustomTabPanel>
            <CustomTabPanel value={currentTabIndex} index={1}>
              {record?.dataRoom && record?.dataRoom.length === 0 ? (
                noData()
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    {record?.dataRoom &&
                      record?.dataRoom
                        .slice(0, Math.ceil(record?.dataRoom.length / 2))
                        .map((item: any, index: number) => {
                          return (
                            <Typography
                              key={index - 1}
                              variant="bodyCopyBold"
                              paragraph
                            >
                              <Link
                                href={item.url}
                                target="_blank"
                                underline="always"
                                color={"inherit"}
                                sx={{
                                  "&:hover": {
                                    cursor: "pointer",
                                  },
                                }}
                              >
                                {item.title}
                              </Link>
                            </Typography>
                          );
                        })}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {record?.dataRoom &&
                      record?.dataRoom
                        .slice(Math.ceil(record?.dataRoom.length / 2))
                        .map((item: any, index: number) => {
                          return (
                            <Typography
                              key={index - 0}
                              variant="bodyCopyBold"
                              paragraph
                            >
                              <Link
                                href={item.url}
                                target="_blank"
                                underline="always"
                                color={"inherit"}
                                sx={{
                                  "&:hover": {
                                    cursor: "pointer",
                                  },
                                }}
                              >
                                {item.title}
                              </Link>
                            </Typography>
                          );
                        })}
                  </Grid>
                </Grid>
              )}
            </CustomTabPanel>
            <CustomTabPanel value={currentTabIndex} index={2}>
              {record?.updates && record?.updates.length === 0 ? (
                noData()
              ) : (
                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      size="large"
                      variant="outlined"
                      sx={{
                        m: 2,
                      }}
                      startIcon={<SettingsIcon />}
                      onClick={() => setUpdateModalOpen(true)}
                    >
                      Notify users{" "}
                    </Button>
                  </Grid>
                  <Grid container spacing={2} padding={3}>
                    <Grid item xs={12} sm={6}>
                      {record?.updates &&
                        record?.updates
                          .slice(0, Math.ceil(record?.updates.length / 2))
                          .map((item: any, index: number) => {
                            return (
                              <Typography
                                key={index - 1}
                                variant="bodyCopyBold"
                                paragraph
                              >
                                <Link
                                  href={item.url}
                                  target="_blank"
                                  underline="always"
                                  color={"inherit"}
                                  sx={{
                                    "&:hover": {
                                      cursor: "pointer",
                                    },
                                  }}
                                >
                                  {item.title}
                                </Link>
                              </Typography>
                            );
                          })}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {record?.updates &&
                        record?.updates
                          .slice(Math.ceil(record?.updates.length / 2))
                          .map((item: any, index: number) => {
                            return (
                              <Typography
                                key={index - 0}
                                variant="bodyCopyBold"
                                paragraph
                              >
                                <Link
                                  href={item.url}
                                  target="_blank"
                                  underline="always"
                                  color={"inherit"}
                                  sx={{
                                    "&:hover": {
                                      cursor: "pointer",
                                    },
                                  }}
                                >
                                  {item.title}
                                </Link>
                              </Typography>
                            );
                          })}
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </CustomTabPanel>
            <CustomTabPanel value={currentTabIndex} index={3}>
              {record?.marketValueHistories &&
                record?.marketValueHistories.length === 0 ? (
                noData()
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    {record?.marketValueHistories &&
                      record?.marketValueHistories
                        .slice(
                          0,
                          Math.ceil(record?.marketValueHistories.length / 2)
                        )
                        .map((item: any, index: number) => {
                          return (
                            <Card
                              key={`{column-1-${index}}`}
                              sx={{ m: 2, backgroundColor: warm[200] }}
                            >
                              <CardContent sx={{ "&:last-child": { pb: 5 } }}>
                                <Typography
                                  key={index - 1}
                                  variant="bodyCopyBold"
                                  paragraph
                                >
                                  History record ID:{" "}
                                  {item.marketValueId
                                    ? item.marketValueId
                                    : "No Data"}
                                </Typography>
                                <Typography variant="bodyCopyBold" paragraph>
                                  Creation Date:{" "}
                                  {item.createdAt ? item.createdAt : "No Data"}
                                </Typography>
                                <Typography
                                  key={index - 1}
                                  variant="bodyCopyBold"
                                  paragraph
                                >
                                  Value:{" "}
                                  {item.value !== null &&
                                  item.value !== undefined
                                    ? item.value
                                    : "No Data"}
                                </Typography>
                                <Typography variant="bodyCopyBold" paragraph>
                                  RVPI:{" "}
                                  {item.moicValue !== null &&
                                  item.moicValue !== undefined
                                    ? item.moicValue
                                    : "No Data"}
                                </Typography>
                              </CardContent>
                            </Card>
                          );
                        })}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {record?.marketValueHistories &&
                      record?.marketValueHistories
                        .slice(
                          Math.ceil(record?.marketValueHistories.length / 2)
                        )
                        .map((item: any, index: number) => {
                          return (
                            <Card
                              key={`{column-2-${index}}`}
                              sx={{ m: 2, backgroundColor: warm[200] }}
                            >
                              <CardContent sx={{ "&:last-child": { pb: 5 } }}>
                                <Typography
                                  key={index - 1}
                                  variant="bodyCopyBold"
                                  paragraph
                                >
                                  History record ID:{" "}
                                  {item.marketValueId
                                    ? item.marketValueId
                                    : "No Data"}
                                </Typography>
                                <Typography variant="bodyCopyBold" paragraph>
                                  Creation Date:{" "}
                                  {item.createdAt ? item.createdAt : "No Data"}
                                </Typography>
                                <Typography
                                  key={index - 1}
                                  variant="bodyCopyBold"
                                  paragraph
                                >
                                  Value:{" "}
                                  {item.value !== null &&
                                  item.value !== undefined
                                    ? item.value
                                    : "No Data"}
                                </Typography>
                                <Typography variant="bodyCopyBold" paragraph>
                                  RVPI:{" "}
                                  {item.moicValue !== null &&
                                  item.moicValue !== undefined
                                    ? item.moicValue
                                    : "No Data"}
                                </Typography>
                              </CardContent>
                            </Card>
                          );
                        })}
                  </Grid>
                </Grid>
              )}
            </CustomTabPanel>
            <CustomTabPanel value={currentTabIndex} index={4}>
              {id && (
                <GrantVestmentRequestsList grantVestmentId={parseFloat(id)} />
              )}
            </CustomTabPanel>
            <CustomTabPanel value={currentTabIndex} index={5}>
              {id && (
                <GrantVestmentDistributionList
                  grantVestmentId={parseFloat(id)}
                />
              )}
            </CustomTabPanel>
            <CustomTabPanel value={currentTabIndex} index={6}>
              <Grid container>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    size="large"
                    variant="outlined"
                    sx={{
                      m: 2,
                    }}
                    startIcon={<SettingsIcon />}
                    onClick={() => setUpdateVisibilityModalOpen(true)}
                  >
                    Manage visibility{" "}
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  {record?.updates && record?.updates.length === 0 ? (
                    noData()
                  ) : (
                    <Grid container spacing={2}>
                      <Grid container rowSpacing={2} mt={3}>
                        {record?.grantAuthorizedViewers?.length !== 0 ? (
                          <List
                            className="successor"
                            sx={{
                              width: "100%",
                              bgcolor: "background.paper",

                              [` & .${listItemClasses.root}:hover`]: {
                                backgroundColor: warm[200],
                                cursor: "pointer",
                              },
                            }}
                          >
                            {record?.grantAuthorizedViewers?.map(
                              (
                                item: GrantVestmentAuthorizedViewers,
                                index: number
                              ) => (
                                <ListItem key={`visibility-${index}`}>
                                  <Grid
                                    alignItems="center"
                                    container
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      px: 2,
                                    }}
                                  >
                                    <Grid item xs={12} sm={6} textAlign="start">
                                      <ListItemText
                                        sx={{
                                          display: "inline-grid",
                                          width: "auto",
                                          flex: "none",
                                        }}
                                        primary={
                                          <UserAvatar user={item.user} />
                                        }
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={6} textAlign="end">
                                      <Button
                                        disabled={loadingDelete}
                                        variant="contained"
                                        startIcon={<DeleteOutlineIcon />}
                                        onClick={() => handleDelete(item)}
                                      >
                                        Delete
                                      </Button>
                                    </Grid>
                                  </Grid>
                                  <Divider />
                                </ListItem>
                              )
                            )}
                          </List>
                        ) : (
                          <Typography
                            variant="smallCopyBold"
                            sx={{ paddingLeft: 3 }}
                          >
                            No users found.
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </CustomTabPanel>
            <CustomTabPanel value={currentTabIndex} index={7}>
              {" "}
              {id && (
                <GrantVestmentLikesList grantVestmentId={parseFloat(id)} />
              )}
            </CustomTabPanel>
            <CustomTabPanel value={currentTabIndex} index={8}>
              {" "}
              {id && (
                <GrantVestmentFollowersList grantVestmentId={parseFloat(id)} />
              )}
            </CustomTabPanel>
          </Box>
          <UpdateMarketValueModal
            open={confirmationModalOpen}
            setOpen={setConfirmationModalOpen}
            grantvestmentId={record?.grantVestmentId}
            currentValue={
              record?.marketValue ? record?.marketValue : record?.raisedAmount
            }
            raisedAmount={record?.raisedAmount}
            grantvestmentMoic={record?.moicValue}
            refetch={refetch}
          />
          <GrantvestmentUpdatesVisibilityModal
            refetch={refetch}
            visible={updateVisibilityModalOpen}
            close={setUpdateVisibilityModalOpen}
            grantvestmentId={id as any}
            authorizedViewers={record?.grantAuthorizedViewers}
          />
          <NotifyUserModal
            open={updateModalOpen}
            onClose={() => setUpdateModalOpen(false)}
            grantvestmentId={record?.grantVestmentId}
            grantvestmentName={record?.name}
          />
        </>
      )}
    </Show>
  );
};
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
export default function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ m: 2 }}>{children}</Box>}
    </div>
  );
}
